import useSWR from 'swr';

import { fetchEndpointData } from '@utils/fetchEndpointData';
import type { IPdfJobRef } from 'src/queue/pdf-job-queues';
import type {
  ExtractLanesTriggerBody,
  ExtractLanesTriggerResponse,
  ExtractLanesPollResponse,
  QuotationDraftsResponse,
} from './endpoints/ExtractLanesEndpoint.types';
import type { QuotationsResponse } from './endpoints/QuotationsEndpoint.types';
import type { QuotationDetail } from './endpoints/QuotationEndpoint.types';
import type { QuotationRollingAverageResponse } from './endpoints/QuotationRollingAverageEndpoint.types';
import type { CustomerPortalQuotationsResponse } from './endpoints/CustomerPortalQuotationsEndpoint.types';
import type { CustomerPortalQuotationDetail } from './endpoints/CustomerPortalQuotationEndpoint.types';
import type { CreateQuotationBody, CreateQuotationResponse } from './endpoints/CreateQuotationEndpoint.types';
import type { UpdateQuotationBody, UpdateQuotationResponse } from './endpoints/UpdateQuotationEndpoint.types';
import type { DeleteQuotationResponse } from './endpoints/DeleteQuotationEndpoint.types';
import type { SendQuotationEmailResponse } from './endpoints/SendQuotationEmailEndpoint.types';

export const QUOTATION_SWR_KEY = 'quotation';
export const CUSTOMER_PORTAL_QUOTATION_SWR_KEY = 'customer-portal-quotation';

export interface QuotationsFilters {
  search?: string;
  customerId?: number;
}

export async function getQuotations(
  cursor?: string | null,
  take?: number,
  filters?: QuotationsFilters,
  sortBy?: string,
  sortOrder?: string,
): Promise<QuotationsResponse> {
  const params = new URLSearchParams();
  if (cursor) params.set('id', cursor);
  if (take != null) params.set('take', take.toString());
  if (filters?.search) params.set('search', filters.search);
  if (filters?.customerId != null) params.set('customerId', filters.customerId.toString());
  if (sortBy) params.set('sortBy', sortBy);
  if (sortOrder) params.set('sortOrder', sortOrder);

  const queryString = params.toString();
  const url = queryString ? `/api/v1/quotations?${queryString}` : '/api/v1/quotations';
  return fetchEndpointData<QuotationsResponse>(url);
}

export async function getQuotation(id: string): Promise<QuotationDetail> {
  return fetchEndpointData<QuotationDetail>(`/api/v1/quotations/${id}`);
}

export interface QuotationRollingAverageFilter {
  customerId?: number;
  type: string;
  departurePostalCode: string;
  departureCountry: string;
  arrivalPostalCode: string;
  arrivalCountry: string;
}

export async function getQuotationRollingAverage(filter: QuotationRollingAverageFilter): Promise<number> {
  const params = new URLSearchParams();
  if (filter.customerId != null) params.set('customerId', filter.customerId.toString());
  params.set('type', filter.type);
  params.set('departurePostalCode', filter.departurePostalCode);
  params.set('departureCountry', filter.departureCountry);
  params.set('arrivalPostalCode', filter.arrivalPostalCode);
  params.set('arrivalCountry', filter.arrivalCountry);

  const res = await fetchEndpointData<QuotationRollingAverageResponse>(
    `/api/v1/quotations/rolling-average?${params.toString()}`,
  );
  // The resolver returned a (float) number that the client coalesced with `?? 0`.
  return res.average ?? 0;
}

export interface CustomerPortalQuotationsFilters {
  search?: string;
}

export async function getCustomerPortalQuotations(
  customerId: number,
  cursor?: string | null,
  take?: number,
  filters?: CustomerPortalQuotationsFilters,
): Promise<CustomerPortalQuotationsResponse> {
  const params = new URLSearchParams();
  params.set('customerId', customerId.toString());
  if (cursor) params.set('id', cursor);
  if (take != null) params.set('take', take.toString());
  if (filters?.search) params.set('search', filters.search);

  return fetchEndpointData<CustomerPortalQuotationsResponse>(`/api/v1/customer-portal/quotations?${params.toString()}`);
}

export async function getCustomerPortalQuotation(id: string): Promise<CustomerPortalQuotationDetail> {
  return fetchEndpointData<CustomerPortalQuotationDetail>(`/api/v1/customer-portal/quotations/${id}`);
}

// The GraphQL CreateQuotationInputData carried quotationDate / expiresAt as Date
// scalars; over REST the client sends the same date strings it produced before
// and the server coerces them to Dates.
export type CreateQuotationInput = Omit<CreateQuotationBody, 'quotationDate' | 'expiresAt'> & {
  quotationDate: string;
  expiresAt: string;
};

export async function createQuotation(data: CreateQuotationInput): Promise<CreateQuotationResponse> {
  return fetchEndpointData<CreateQuotationResponse>('/api/v1/quotations', {
    method: 'POST',
    body: data,
  });
}

export type UpdateQuotationInput = Omit<UpdateQuotationBody, 'quotationDate' | 'expiresAt'> & {
  quotationDate?: string;
  expiresAt?: string;
};

export async function updateQuotation(
  quotationId: string,
  data: UpdateQuotationInput,
): Promise<UpdateQuotationResponse> {
  return fetchEndpointData<UpdateQuotationResponse>(`/api/v1/quotations/${quotationId}`, {
    method: 'PATCH',
    body: data,
  });
}

export async function deleteQuotation(quotationId: string): Promise<DeleteQuotationResponse> {
  return fetchEndpointData<DeleteQuotationResponse>(`/api/v1/quotations/${quotationId}`, {
    method: 'DELETE',
  });
}

export async function generateQuotationPdf(quotationId: string): Promise<IPdfJobRef> {
  // Returns the enqueued PDF job reference ({ queue, jobId }) that the client
  // polls via runPdfJobAndRefresh — matches the document-pdf queue contract.
  return fetchEndpointData<IPdfJobRef>(`/api/v1/quotations/${quotationId}/generate`, {
    method: 'POST',
  });
}

export async function sendQuotationEmail(quotationId: string, contacts: number[]): Promise<SendQuotationEmailResponse> {
  return fetchEndpointData<SendQuotationEmailResponse>(`/api/v1/quotations/${quotationId}/send`, {
    method: 'POST',
    body: { contacts },
  });
}

export function useQuotation(id: string) {
  return useSWR([QUOTATION_SWR_KEY, id], () => getQuotation(id));
}

export function useCustomerPortalQuotation(id: string) {
  return useSWR([CUSTOMER_PORTAL_QUOTATION_SWR_KEY, id], () => getCustomerPortalQuotation(id));
}

export async function triggerExtractLanes(body: ExtractLanesTriggerBody): Promise<ExtractLanesTriggerResponse> {
  return fetchEndpointData<ExtractLanesTriggerResponse>('/api/v1/quotation/extract-lanes', {
    method: 'POST',
    body,
  });
}

export async function pollExtractLanes(draftId: string): Promise<ExtractLanesPollResponse> {
  return fetchEndpointData<ExtractLanesPollResponse>(
    `/api/v1/quotation/extract-lanes?draftId=${encodeURIComponent(draftId)}`,
  );
}

export async function getQuotationDrafts(): Promise<QuotationDraftsResponse> {
  return fetchEndpointData<QuotationDraftsResponse>('/api/v1/quotation/drafts');
}

export async function deleteQuotationDraft(draftId: string): Promise<void> {
  await fetchEndpointData(`/api/v1/quotation/drafts/${encodeURIComponent(draftId)}`, {
    method: 'DELETE',
  });
}
